import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import ScrollToTop from './components/ScrollToTop';
import '@/App.css';
import Landing from './pages/Landing';
import Blog from './pages/Blog';
import Article from './pages/Article';
import Unsubscribe from './pages/Unsubscribe';
import ForFounders from './pages/ForFounders';
import ForInvestors from './pages/ForInvestors';
import ForCofounders from './pages/ForCofounders';
import Pricing from './pages/Pricing';
import FounderPricing from './pages/pricing/FounderPricing';
import InvestorPricing from './pages/pricing/InvestorPricing';
import CofounderPricing from './pages/pricing/CofounderPricing';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Help from './pages/Help';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Account from './pages/Account';
import Login from './pages/Login';
import Register from './pages/Register';
import FounderDashboard from './pages/FounderDashboard';
import InvestorDashboard from './pages/InvestorDashboard';
import CofounderDashboard from './pages/CofounderDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserDetail from './pages/AdminUserDetail';
import WaitlistAdmin from './pages/WaitlistAdmin';
import Discovery from './pages/Discovery';
import Matches from './pages/Matches';
import Messages from './pages/Messages';
import ProfileSetup from './pages/ProfileSetup';
import ProfileView from './pages/ProfileView';
import ProfileViews from './pages/ProfileViews';
import PitchSessions from './pages/PitchSessions';
import RelationshipMilestones from './pages/RelationshipMilestones';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

function PrivateRoute({ children, role }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-slate-300">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  const getDashboard = () => {
    if (!user) return <Navigate to="/login" replace />;
    
    switch (user.role) {
      case 'founder':
        return <FounderDashboard />;
      case 'investor':
        return <InvestorDashboard />;
      case 'cofounder':
        return <CofounderDashboard />;
      case 'admin':
        return <AdminDashboard />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Routes>
      <Route path="/" element={user ? getDashboard() : <Landing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:articleId" element={<Article />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/for-founders" element={<ForFounders />} />
      <Route path="/for-investors" element={<ForInvestors />} />
      <Route path="/for-cofounders" element={<ForCofounders />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/pricing/founder" element={<FounderPricing />} />
      <Route path="/pricing/investor" element={<InvestorPricing />} />
      <Route path="/pricing/cofounder" element={<CofounderPricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/profile-setup" element={<PrivateRoute><ProfileSetup /></PrivateRoute>} />
      <Route path="/discovery" element={<PrivateRoute><Discovery /></PrivateRoute>} />
      <Route path="/matches" element={<PrivateRoute><Matches /></PrivateRoute>} />
      <Route path="/messages/:matchId" element={<PrivateRoute><Messages /></PrivateRoute>} />
      <Route path="/profile/:userId" element={<PrivateRoute><ProfileView /></PrivateRoute>} />
      <Route path="/profile-views" element={<PrivateRoute><ProfileViews /></PrivateRoute>} />
      <Route path="/pitch-sessions" element={<PrivateRoute><PitchSessions /></PrivateRoute>} />
      <Route path="/relationships" element={<PrivateRoute><RelationshipMilestones /></PrivateRoute>} />
      <Route path="/account" element={<PrivateRoute><Account /></PrivateRoute>} />
      <Route path="/admin/users/:userId" element={<PrivateRoute role="admin"><AdminUserDetail /></PrivateRoute>} />
      <Route path="/waitlist-admin" element={<WaitlistAdmin />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
            <Toaster position="top-right" closeButton richColors />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
